import type React from 'react';
import { useEffect } from 'react';

const DEFAULT_OBSERVER_OPTIONS = { threshold: 0.5 };

const intersectionHandler: (
  callback: Callback
) => IntersectionObserverCallback =
  (callback) =>
  (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
    const [entry] = entries;

    if (entry.isIntersecting) {
      if (observer) {
        observer.disconnect();
      }
      callback();
    }
  };

type Callback = () => void;

type UseOnVisible = ({
  callback,
  observerOptions,
  targetElementRef,
}: {
  callback: Callback;
  observerOptions?: IntersectionObserverInit;
  targetElementRef: React.MutableRefObject<HTMLElement | null>;
}) => void;

export const useOnVisible: UseOnVisible = ({
  callback,
  observerOptions = DEFAULT_OBSERVER_OPTIONS,
  targetElementRef,
}) => {
  useEffect(() => {
    let observer: IntersectionObserver;
    const targetElement = targetElementRef.current;

    if (targetElement) {
      observer = new IntersectionObserver(
        intersectionHandler(callback),
        observerOptions
      );
      observer.observe(targetElement);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [callback, observerOptions, targetElementRef]);
};
